
import "./ArchivedRule.css";
import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "react-bootstrap";
import searchruleIcon from "../../../common/assets/images/searchrule.png";
import RefreshIcon from "../../../common/assets/images/reload.png";
import {
  fetchArchivedRules,
  fetchPathwayEntryOptions,
  fetchArchivedByOptions,
  setRuleIdFilter,
  setDescriptionFilter,
  setPathwayEntryFilter,
  setTriggerCodeFilter,
  setPageNumber,
  resetArchivedRulesFilters,
  setSelectedRuleId,
  setArchivedDateFilter,
  setArchivedByFilter,
} from "./redux/Archivedrulesslice";
import { Tooltip } from "@mui/material";


const SEARCH_TYPE_OPTIONS = [
  { value: "rule_id", label: "Search by Rule ID" },
  { value: "description", label: "Search by Rule Description" },
  { value: "trigger_codes", label: "Search by Trigger Codes" },
];

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const SEARCH_DEBOUNCE_MS = 400;

function formatDisplayDate(isoDate) {
  if (!isoDate) return "-";
  const d = new Date(isoDate);
  if (isNaN(d)) return isoDate;
  const datePart = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart}, ${timePart} (UTC)`;

}

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getCalendarMatrix(year, month) {
  const firstDay = new Date(year, month, 1);
  const startWeekday = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells = [];

  for (let i = startWeekday - 1; i >= 0; i--) {
    cells.push({ current: false, date: new Date(year, month - 1, daysInPrevMonth - i) });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ current: true, date: new Date(year, month, d) });
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(next.getDate() + 1);
    cells.push({ current: false, date: next });
  }

  return cells;
}
function formatFilterDate(isoDate) {
  if (!isoDate) return "-";
  const d = new Date(isoDate);
  if (isNaN(d)) return isoDate;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}


export default function ArchivedRules() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    rules,
    cards,
    pageNumber,
    pageSize,
    totalCount,
    rulesLoading,
    pathwayOptions,
    pathwayOptionsLoading,
    archivedByOptions,
    archivedByOptionsLoading,
    filters,
  } = useSelector((state) => state.archiveRule);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("rule_id");
  const [showSearchTypeMenu, setShowSearchTypeMenu] = useState(false);

  const [pathwayFilter, setPathwayFilter] = useState("");
  const [showPathwayMenu, setShowPathwayMenu] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // const [archivedByFilter, setArchivedByFilter] = useState("");
  const [showArchivedByMenu, setShowArchivedByMenu] = useState(false);

  const searchWrapRef = useRef(null);
  const dateWrapRef = useRef(null);
  const pathwayWrapRef = useRef(null);
  const archivedByWrapRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const archivedDateFilter = filters.archieved_date;
  const archivedByFilter = filters.archived_by?.[0] || "";

  useEffect(() => {
    dispatch(fetchPathwayEntryOptions());
    dispatch(fetchArchivedByOptions());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchArchivedRules());
  }, [dispatch, pageNumber]);


  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    searchDebounceRef.current = setTimeout(() => {
      if (searchType === "rule_id") {
        dispatch(setRuleIdFilter(searchTerm.trim()));
      } else if (searchType === "description") {
        dispatch(setDescriptionFilter(searchTerm.trim()));
      } else if (searchType === "trigger_codes") {
        const codes = searchTerm
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean); // drops empty entries from trailing commas etc.
        dispatch(setTriggerCodeFilter(codes));
      }
      dispatch(fetchArchivedRules());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(searchDebounceRef.current);
  }, [dispatch, searchTerm, searchType]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setShowSearchTypeMenu(false);
      }
      if (dateWrapRef.current && !dateWrapRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
      if (pathwayWrapRef.current && !pathwayWrapRef.current.contains(e.target)) {
        setShowPathwayMenu(false);
      }
      if (archivedByWrapRef.current && !archivedByWrapRef.current.contains(e.target)) {
        setShowArchivedByMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const calendarCells = useMemo(
    () => getCalendarMatrix(calendarMonth.getFullYear(), calendarMonth.getMonth()),
    [calendarMonth]
  );

  const displayedRules = useMemo(() => {
    return rules;
  }, [rules]);

  const handleViewRule = (rule) => {
    dispatch(setSelectedRuleId(rule.code_assist_manual_rule_id));
    const queryParams = new URLSearchParams({
      code_assist_manual_rule_id: rule.code_assist_manual_rule_id,
    });
    navigate(`/archived-rule-details?${queryParams.toString()}`);
    closeAllMenus();
  };

  const handleEditRule = (rule) => {
    dispatch(setSelectedRuleId(rule.code_assist_manual_rule_id));
    navigate(`/archived-edit-rule`);
    // navigate(`/archived-edit-rule/${rule.code_assist_manual_rule_id}`);
    closeAllMenus();
  };

  const handleRuleIdClick = (rule) => {
    dispatch(setSelectedRuleId(rule.code_assist_manual_rule_id));
    // navigate(`/archived-rule-details`);
    navigate(`/archived-rule-details/${rule.code_assist_manual_rule_id}`);
    closeAllMenus();
  };

  const handleSelectSearchType = (type) => {
    setSearchType(type);
    setShowSearchTypeMenu(false);
    setSearchTerm("");
  };

  const closeAllMenus = () => {
    setShowSearchTypeMenu(false);
    setShowDatePicker(false);
    setShowPathwayMenu(false);
    setShowArchivedByMenu(false);
  };

  const handleOpenPathwayMenu = () => {
    const next = !showPathwayMenu;
    closeAllMenus();
    setShowPathwayMenu(next);
  };

  const handleSelectPathway = (value) => {
    setPathwayFilter(value);
    dispatch(setPathwayEntryFilter(value));
    dispatch(fetchArchivedRules());
    setShowPathwayMenu(false);
  };

  const handleOpenArchivedByMenu = () => {
    const next = !showArchivedByMenu;
    closeAllMenus();
    setShowArchivedByMenu(next);
  };

  const handleSelectArchivedBy = (value) => {
    dispatch(setArchivedByFilter(value));
    dispatch(fetchArchivedRules());
    setShowArchivedByMenu(false);
  };

  const handleOpenDatePicker = () => {
    setCalendarMonth(archivedDateFilter ? new Date(archivedDateFilter) : new Date());
    const next = !showDatePicker;
    closeAllMenus();
    setShowDatePicker(next);
  };

  const shiftCalendarMonth = (delta) => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const handleSelectCalendarDay = (date) => {
    const isoDate = toISODate(date);
    dispatch(setArchivedDateFilter(isoDate));
    dispatch(fetchArchivedRules());
    setShowDatePicker(false);
  };
  const handleClearDate = () => {
    dispatch(setArchivedDateFilter(""));
    dispatch(fetchArchivedRules());
    setShowDatePicker(false);
  }
  const handleTodayDate = () => {
    const today = new Date();
    const isoDate = toISODate(today);
    setCalendarMonth(today);
    dispatch(setArchivedDateFilter(isoDate));
    dispatch(fetchArchivedRules());
    setShowDatePicker(false);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setSearchType("rule_id");
    setPathwayFilter("");
    // setArchivedDateFilter("");
    setArchivedByFilter("");
    closeAllMenus();

    dispatch(resetArchivedRulesFilters());
    dispatch(fetchArchivedRules());
    dispatch(fetchPathwayEntryOptions());
    dispatch(fetchArchivedByOptions());
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) dispatch(setPageNumber(pageNumber - 1));
  };

  const handleNextPage = () => {
    if (pageNumber * pageSize < totalCount) dispatch(setPageNumber(pageNumber + 1));
  };

  const activeSearchLabel =
    SEARCH_TYPE_OPTIONS.find((o) => o.value === searchType)?.label ||
    "Search rules...";

  const todayISO = toISODate(new Date());

  const rangeStart = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const rangeEnd = Math.min(pageNumber * pageSize, totalCount);

  return (
    <div className="archivedRules-page">
      <div className="archivedRules-header">
        <h1 className="archivedRules-title">Archived Rules</h1>
        <p className="archivedRules-subtitle">
          View archived guidance & decision support rules retained for
          audit and historical review.
        </p>
      </div>

      <div className="archivedRules-statsRow">
        <div className="archivedRules-statCard">
          <div className="archivedRules-statLabel">Total Archived Rules</div>
          <div className="archivedRules-statValue">
            {cards.total_archieved_rules}
          </div>
        </div>
        <div className="archivedRules-statCard">
          <div className="archivedRules-statLabel">Archived This Month</div>
          <div className="archivedRules-statValue">
            {cards.archieved_this_month}
          </div>
        </div>
        <div className="archivedRules-statCard">
          <div className="archivedRules-statLabel">Last 7 Days</div>
          <div className="archivedRules-statValue">{cards.last_7days_archieved}</div>
        </div>
      </div>

      <div className="archivedRules-toolbarCard">
        <div className="archivedRules-searchWrapper" ref={searchWrapRef}>
          <span className="archivedRules-searchIcon" aria-hidden="true">
            <img src={searchruleIcon} alt="search" />
          </span>
          <input
            type="text"
            className="archivedRules-searchInput"
            placeholder={activeSearchLabel}
            value={searchTerm}
            onFocus={() => setShowSearchTypeMenu(true)}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {showSearchTypeMenu && (
            <div className="archivedRules-searchTypeMenu">
              {SEARCH_TYPE_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelectSearchType(option.value)}
                  className={`archivedRules-searchTypeItem ${option.value === searchType
                    ? "archivedRules-searchTypeItem--active"
                    : ""
                    }`}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="archivedRules-filters">
          {/* Pathway Entry Point - custom dropdown */}
          <div className="archivedRules-selectWrapper" ref={pathwayWrapRef}>
            <button
              type="button"
              className="archivedRules-filterBtn"
              onClick={handleOpenPathwayMenu}
            >
              <span className="archivedRules-filterBtnLabel">
                {pathwayFilter || "Pathway Entry Point"}
              </span>
              <span className="archivedRules-filterCaret" aria-hidden="true">
                &#9662;
              </span>
            </button>

            {showPathwayMenu && (
              <div className="archivedRules-dropdownMenu">
                {pathwayOptionsLoading ? (
                  <div className="archivedRules-dropdownLoading">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading...
                  </div>
                ) : (
                  <>
                    {pathwayOptions.length === 0 ? (
                      <div className="archivedRules-dropdownEmpty">
                        No options found
                      </div>
                    ) : (
                      pathwayOptions.map((opt, idx) => (
                        <div
                          key={`${opt.type}-${opt.value}-${idx}`}
                          onClick={() => handleSelectPathway(opt.value)}
                          className={`archivedRules-dropdownItem ${pathwayFilter === opt.value
                            ? "archivedRules-dropdownItem--active"
                            : ""
                            }`}
                        >
                          {opt.value}
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="archivedRules-selectWrapper" ref={dateWrapRef}>
            <button
              type="button"
              className="archivedRules-filterBtn"
              onClick={handleOpenDatePicker}
            >
              {archivedDateFilter
                ? formatFilterDate(archivedDateFilter)
                : "Archived Date"}
              <span className="archivedRules-filterCaret" aria-hidden="true">
                &#9662;
              </span>
            </button>

            {archivedDateFilter && (
              <button
                type="button"
                className="archivedRules-clearDateBtn"
                aria-label="Clear archived date filter"
                onClick={handleClearDate}
              >
                &#215;
              </button>
            )}

            {showDatePicker && (
              <div className="archivedRules-calendarPopover">
                <div className="archivedRules-calendarHeader">
                  <span className="archivedRules-calendarMonthLabel">
                    {calendarMonth.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <div className="archivedRules-calendarNav">
                    <button
                      type="button"
                      onClick={() => shiftCalendarMonth(-1)}
                      aria-label="Previous month"
                    >
                      &#8593;
                    </button>
                    <button
                      type="button"
                      onClick={() => shiftCalendarMonth(1)}
                      aria-label="Next month"
                    >
                      &#8595;
                    </button>
                  </div>
                </div>

                <div className="archivedRules-calendarWeekdays">
                  {WEEKDAY_LABELS.map((label) => (
                    <span key={label}>{label}</span>
                  ))}
                </div>

                <div className="archivedRules-calendarGrid">
                  {calendarCells.map((cell, idx) => {
                    const iso = toISODate(cell.date);
                    const isSelected = archivedDateFilter === iso;
                    const isToday = todayISO === iso;
                    const isFuture = iso > todayISO;
                    return (
                      <button
                        type="button"
                        key={idx}
                        disabled={isFuture}
                        className={[
                          "archivedRules-calendarDay",
                          !cell.current && "archivedRules-calendarDay--muted",
                          isSelected && "archivedRules-calendarDay--selected",
                          isToday && !isSelected && "archivedRules-calendarDay--today",
                          isFuture && "archivedRules-calendarDay--disabled",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => handleSelectCalendarDay(cell.date)}
                      >
                        {cell.date.getDate()}
                      </button>
                    );
                  })}
                </div>

                <div className="archivedRules-calendarFooter">
                  <button
                    type="button"
                    className="archivedRules-calendarLink"
                    onClick={handleClearDate}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="archivedRules-calendarLink"
                    onClick={handleTodayDate}
                  >
                    Today
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Archived By - custom dropdown */}
          <div className="archivedRules-selectWrapper" ref={archivedByWrapRef}>
            <button
              type="button"
              className="archivedRules-filterBtn"
              onClick={handleOpenArchivedByMenu}
            >
              <span className="archivedRules-filterBtnLabel">
                {archivedByFilter || "Archived By"}
              </span>
              <span className="archivedRules-filterCaret" aria-hidden="true">
                &#9662;
              </span>
            </button>

            {showArchivedByMenu && (
              <div className="archivedRules-dropdownMenu archivedRules-dropdownMenu--right">
                {archivedByOptionsLoading ? (
                  <div className="archivedRules-dropdownLoading">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading...
                  </div>
                ) : (
                  <>
                    {archivedByOptions.length === 0 ? (
                      <div className="archivedRules-dropdownEmpty">
                        No options found
                      </div>
                    ) : (
                      archivedByOptions.map((name) => (
                        <div
                          key={name}
                          onClick={() => handleSelectArchivedBy(name)}
                          className={`archivedRules-dropdownItem ${archivedByFilter === name
                            ? "archivedRules-dropdownItem--active"
                            : ""
                            }`}
                        >
                          {name}
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className="archivedRules-refreshBtn"
            aria-label="Reset filters and refresh"
            onClick={handleResetFilters}
          >
            <img src={RefreshIcon} alt="" />
          </button>
        </div>
      </div>

      <div className="archivedRules-tableWrapper">
        <div className="archivedRules-tableScroll">
          <table className="archivedRules-table">
            <thead>
              <tr>
                <th>Rule ID</th>
                <th>Rule Description</th>
                <th>Pathway Entry Point</th>
                <th>Trigger Codes</th>
                <th>Status</th>
                <th>Archived Date</th>
                <th>Archived By</th>
                <th className="archivedRules-th--actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rulesLoading && (
                <tr>
                  <td colSpan={8} className="archivedRules-emptyRow">
                    <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                    Loading archived rules...
                  </td>
                </tr>
              )}

              {!rulesLoading &&
                displayedRules.map((rule) => (
                  <tr key={rule.code_assist_manual_rule_id}>
                    <td>
                      <button
                        type="button"
                        className="archivedRules-ruleIdLink"
                      // onClick={() => handleRuleIdClick(rule)}
                      >
                        {rule.rule_id}
                      </button>
                    </td>
                    <td>
                      {/* {rule.description} */}
                      <Tooltip title={rule.description || "No description provided"}>
                        <span>
                          {rule.description
                            ? rule.description.length > 20
                              ? `${rule.description.slice(0, 20)}...`
                              : rule.description
                            : "No description provided"}
                        </span>
                      </Tooltip>
                      </td>
                    <td>
                      {/* {rule.pathway_entry?.value} */}
                      <Tooltip title={rule.pathway_entry?.value || "No entry point"}>
                        <span>
                          {rule.pathway_entry?.value
                            ? rule.pathway_entry.value.length > 20
                              ? `${rule.pathway_entry.value.slice(0, 20)}...`
                              : rule.pathway_entry.value
                            : "-"}
                        </span>
                      </Tooltip>
                      </td>
                    <td>
                      <div className="archivedRules-tags">
                        
                      {rule.trigger_codes && rule.trigger_codes.length > 0 ? (
                        (rule.trigger_codes || []).map((tc) => (
                          <span className="archivedRules-tag" key={tc.code}>
                            {tc.code}
                          </span>
                        ))
                      ):(
                        <Tooltip title={"No trigger codes"}>
                          <span className="archivedRules-no-codes">-</span>
                        </Tooltip>
                        
                       )}
                      </div>
                    </td>
                    <td>
                      <span className="archivedRules-statusBadge">
                        {(rule.status || "").toUpperCase()}
                      </span>
                    </td>
                    <td>{formatDisplayDate(rule.archived_date)}</td>
                    <td>{rule.created_by}</td>
                    <td>
                      <div className="archivedRules-actions">
                        <button
                          type="button"
                          className="archivedRules-actionBtn"
                          onClick={() => handleViewRule(rule)}
                          aria-label={`View ${rule.code_assist_manual_rule_id}`}
                        >
                          &#128065;
                        </button>
                        <button
                          type="button"
                          className="archivedRules-actionBtn"
                          onClick={() => handleEditRule(rule)}
                          aria-label={`Edit ${rule.code_assist_manual_rule_id}`}
                        >
                          &#9998;
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!rulesLoading && displayedRules.length === 0 && (
                <tr>
                  <td colSpan={8} className="archivedRules-emptyRow">
                    No matching archived rules.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="archivedRules-footer">
          <div className="archivedRules-footerText">
            {totalCount === 0
              ? `Showing 0 of 0 archived rules`
              : `Showing ${rangeStart} to ${rangeEnd} of ${totalCount} archived rules`}
          </div>
          <div className="archivedRules-pagination">
            <button
              type="button"
              className="archivedRules-pageBtn"
              onClick={handlePreviousPage}
              disabled={pageNumber <= 1 || rulesLoading}
            >
              Previous
            </button>
            <button
              type="button"
              className="archivedRules-pageBtn"
              onClick={handleNextPage}
              disabled={pageNumber * pageSize >= totalCount || rulesLoading}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}